import React, {useCallback, useMemo, useState} from 'react';
import Card from 'react-bootstrap/Card';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import Pagination from 'react-js-pagination';
import {useIntl} from 'react-intl';

import Loader from '../dashboard/Loader';
import ErrorModal from '../ui/ErrorModal';
import API from '../../common/utils/API';
import {VenueLocalDateTimeCell} from './formatDateTime';
import {BandBadge} from './BandBadge';
import PromotedFilterBar from './PromotedFilterBar';
import {usePromotedFetch} from './usePromotedFetch';
import {
    COMPUTED_PAGE_SIZE,
    TABLE_SCROLL_HEIGHT,
    YesNo,
    emptyMessageId,
    notPromotedReasonIds,
} from './promotedShared';

const fetchComputed = (params) =>
    API.reports.getRelevancePromotedComputed(params);

/**
 * Fila memoizada: un cambio de `loading` o de página no vuelve a pintar las
 * filas cuyo evento no cambió (rerender-memo). `onSelectEvent` llega estable
 * desde `RelevancePage` (setter de useState).
 */
const ComputedRow = React.memo(function ComputedRow({event, onSelectEvent}) {
    const intl = useIntl();
    const eligibility = event.eligibility;
    const reasons = notPromotedReasonIds(event).map((id) =>
        intl.formatMessage(
            {id},
            {min: eligibility ? eligibility.minPaidBuyers : ''},
        ),
    );
    return (
        <tr
            data-test='promotedComputedRow'
            onClick={() => onSelectEvent(event.eventId)}
            style={{cursor: 'pointer'}}
        >
            <td>{event.eventId}</td>
            <td>{event.name}</td>
            <td>
                <VenueLocalDateTimeCell iso={event.startsAt} />
            </td>
            <td>
                <BandBadge intl={intl} band={event.band} />
            </td>
            <td data-test='promotedComputedPromoted'>
                <YesNo value={event.wouldPromote} />
            </td>
            <td>{event.score}</td>
            <td>{event.n}</td>
            <td data-test='promotedComputedPaidBuyers'>
                {eligibility ? eligibility.paidBuyers : '—'}
            </td>
            <td>{event.z}</td>
            <td data-test='promotedComputedAutoRanking'>
                <YesNo value={event.autoRankingEnabled} />
            </td>
            <td data-test='promotedComputedDemandBanner'>
                <YesNo value={event.demandBannerEnabled} />
            </td>
            <td data-test='promotedComputedReason'>{reasons.join(' · ')}</td>
        </tr>
    );
});

/**
 * Tabla "Computados": filas del motor por score, paginadas en el servidor,
 * con su propia barra de filtros. Aplicar o limpiar un filtro vuelve a la
 * página 1.
 */
function ComputedPromotedTable({onSelectEvent}) {
    const intl = useIntl();
    const t = (id) => intl.formatMessage({id});
    const [query, setQuery] = useState({filters: {}, page: 1});
    const params = useMemo(
        () => ({limit: COMPUTED_PAGE_SIZE, page: query.page, ...query.filters}),
        [query],
    );
    const {loading, error, data, refresh, dismissError} = usePromotedFetch(
        fetchComputed,
        params,
    );
    const items = (data && data.items) || [];
    const total = (data && data.total) || 0;
    const limit = (data && data.limit) || COMPUTED_PAGE_SIZE;
    const filtersActive = Object.keys(query.filters).length > 0;

    const applyFilters = useCallback(
        (filters) => setQuery({filters, page: 1}),
        [],
    );
    const clearFilters = useCallback(
        () => setQuery({filters: {}, page: 1}),
        [],
    );
    const changePage = useCallback(
        (page) => setQuery((previous) => ({...previous, page})),
        [],
    );

    return (
        <Card className='mb-4' data-test='promotedComputedCard'>
            <Card.Header className='d-flex justify-content-between align-items-center'>
                {intl.formatMessage(
                    {id: 'RELEVANCE.PROMOTED_COMPUTED_HEADER'},
                    {shown: items.length, total, limit},
                )}
                <Button
                    variant='secondary'
                    size='sm'
                    onClick={refresh}
                    data-test='promotedComputedRefresh'
                >
                    {t('RELEVANCE.REFRESH_BUTTON')}
                </Button>
            </Card.Header>
            <Card.Body style={{position: 'relative'}}>
                <Loader loading={loading} />
                <PromotedFilterBar
                    testIdPrefix='promotedComputed'
                    showPromoted
                    label={t('RELEVANCE.PROMOTED_FILTER_COMPUTED_LABEL')}
                    active={filtersActive}
                    onApply={applyFilters}
                    onClear={clearFilters}
                />
                {items.length === 0 && !loading ? (
                    <p data-test='promotedComputedEmpty'>
                        {t(emptyMessageId(filtersActive))}
                    </p>
                ) : (
                    <div
                        data-test='promotedComputedTableScroll'
                        style={{
                            maxHeight: TABLE_SCROLL_HEIGHT,
                            overflowY: 'auto',
                        }}
                    >
                        <Table
                            striped
                            bordered
                            hover
                            size='sm'
                            data-test='promotedComputedTable'
                        >
                            <thead>
                                <tr>
                                    <th>{t('RELEVANCE.PROMOTED_TABLE_ID')}</th>
                                    <th>
                                        {t('RELEVANCE.PROMOTED_TABLE_NAME')}
                                    </th>
                                    <th>
                                        {t(
                                            'RELEVANCE.PROMOTED_TABLE_STARTS_AT',
                                        )}
                                    </th>
                                    <th>
                                        {t('RELEVANCE.PROMOTED_TABLE_BAND')}
                                    </th>
                                    <th>
                                        {t('RELEVANCE.PROMOTED_TABLE_PROMOTED')}
                                    </th>
                                    <th>
                                        {t('RELEVANCE.PROMOTED_TABLE_SCORE')}
                                    </th>
                                    <th>{t('RELEVANCE.PROMOTED_TABLE_N')}</th>
                                    <th>
                                        {t(
                                            'RELEVANCE.PROMOTED_TABLE_PAID_BUYERS',
                                        )}
                                    </th>
                                    <th>{t('RELEVANCE.PROMOTED_TABLE_Z')}</th>
                                    <th>
                                        {t(
                                            'RELEVANCE.PROMOTED_TABLE_AUTO_RANKING',
                                        )}
                                    </th>
                                    <th>
                                        {t(
                                            'RELEVANCE.PROMOTED_TABLE_DEMAND_BANNER',
                                        )}
                                    </th>
                                    <th>
                                        {t('RELEVANCE.PROMOTED_TABLE_REASON')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((event) => (
                                    <ComputedRow
                                        key={event.eventId}
                                        event={event}
                                        onSelectEvent={onSelectEvent}
                                    />
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
                {total > limit && (
                    <div className='d-flex justify-content-end mt-2'>
                        <Pagination
                            itemClass='page-item'
                            linkClass='page-link'
                            activePage={query.page}
                            itemsCountPerPage={limit}
                            totalItemsCount={total}
                            pageRangeDisplayed={5}
                            onChange={changePage}
                        />
                    </div>
                )}
            </Card.Body>
            <ErrorModal
                isLoadErrorModalOpen={error}
                toggleLoadErrorModal={dismissError}
                errorMessage={t('RELEVANCE.LOAD_ERROR')}
                handleRetryClick={refresh}
                returnUrl='/relevance'
                loading={loading}
            />
        </Card>
    );
}

export default ComputedPromotedTable;
